import { combineReducers } from "redux";
import { routerReducer as routing } from "react-router-redux";
import { entitiesReducer as entities, queriesReducer as queries } from "redux-query";

import app from "./app";
import { smartOrders } from "models/new/smart-orders/fetch-smart-orders/reducer";
import activeList from "./active-list";
import attachments from "./attachments";
import campaign from "./campaign";
import campaigns from "./campaigns";
import campaignRecipients from "./campaign-recipients";
import cart from "./cart";
import catalogs from "./catalogs";
import catalog from "./catalog";
import catalogShares from "./catalog-shares";
import collectionBudgets from "./collection-budgets";
import { contactGroupReducer as contactGroup } from "./contact-group";
import { contactGroupRecipientsReducer as contactGroupRecipients } from "./contact-group-recipients";
import { filtersReducer as filters } from "./filters";
import items from "./items";
import { itemReducer as item } from "./item";
import bulkItems from "./bulk-items";
import { bulkManagerReducer as bulkManager } from "./bulk-manager";
import { views } from "./views";
import brandProfile from "./brand-profile";
import { ILoaderState, loader } from "./loader";
import brandProfiles from "./brand-profiles";
import brandCategories from "./brand-categories";
import events from "./events";
import ldFlags, { LdFlagsState } from "./ldFlags";
import dnd from "./dnd";
import orderSummary from "./order-summary";
import { schemas } from "./schemas";
import snackbars from "./snackbars";
import { scope, IState as IScope } from "./scope";
import { masterSchema } from "./master-schema";
import { schemaMap } from "./schema-map";
import dashboard, { IState as IDashboard } from "./dashboard";
import { buyerDashboard } from "./buyer-dashboard";
import legacyRetailers from "./legacy-retailers";
import { pageLayout } from "./page-layout";
import { pageLayouts } from "./page-layouts";
import { template, templates } from "./template-reducers";
import { whiteboard } from "./whiteboard";
import { whiteboardPermissions } from "./whiteboard-permissions";
import { whiteboards } from "./whiteboards";
import { collections } from "./collections";
import { collection } from "./collection";
import { mutate } from "./mutate";
import { dimensionsPanel, DimensionsPanelCompositeReduxState } from "./dimensions-panel/dimensions-panel";
import { productReducer as product } from "./product";
import { productsReducer as products } from "./products";
import { entriesReducer as entries } from "./entries";
import { shippingAccounts } from "./shipping-accounts";
import { shippingCarriers } from "./shipping-carriers";
import { shippingCarrierServices } from "./carrier-services";
import { shippingAccountCarriers } from "./shipping-account-carriers";
import { accountCarrierServiceWarehouseService } from "./shipping-account-carrier-warehouse-services";
import { shippingPromotions } from "./shipping-promotions";
import { retailerProfileForm } from "./retailer-profile-form";
import { collectionPermissions } from "./collection-permissions";
import { usersReducer as users } from "./user";
import { userLookupReducer as userLookup } from "./user-lookup";
import { activeStatusReducer as activeStatus } from "./active-status";
import { utilitiesReducer as utilities } from "./utilities";
import { globalProducts } from "./global-products";
import { connectionRequests } from "./connection-requests";
import { customization, ICustomizationState } from "./customization-reducers";
import { promotionsReducer as promotions, orderPromotions } from "./promotions";
import { promotionReducer as promotion } from "./promotion";
import cartGridView, { CartGridView } from "./cart-grid-view";
import { collectionSidePanel } from "./collection-side-panel";
import { collectionChat } from "./collection-chat";
import { authentication } from "./authentication";
import { brandedLogin } from "./branded-login";
import { cartConfirmation } from "./cart-confirmation";
import { lifecycleScripts } from "./lifecycle-scripts-reducers";
import { USER_LOGOUT } from "constants/action-types";
import { internalNotes } from "./internal-notes-reducers";
import { genericFilters } from "./generic-filters";
import { filtersGroups } from "./filters-groups";
import { assortmentBuyers, IAssortmentBuyersState } from "./assortment-buyers-reducers";
import { assortmentSuperusers, IAssortmentSuperusersState } from "./assortment-superusers-reducers";
import { contacts, IContactsState } from "./contacts-reducers";
import { shippingRates } from "./shipping-rates";
import { recentOrders } from "./recent-orders-reducer";
import { companyDiscounts } from "./company-discounts-reducer";
import { warning, IWarningState } from "./warning-reducers";
import { inventoryArriving } from "./inventory-arriving";
import { collectionItemDisplayPreference } from "./collection-item-display-preference";
import { collectionRollupObjects } from "./collection-rollup-objects";
import { IDataFlowState, dataflow } from "./dataflow-reducers";
import { IWorkingState, working } from "reducers/working-order/working";
import { IInventoryDataState, inventoryData, inventoryDataUpload } from "./inventory-data-reducers";
import { ICancelledStylesState, cancelledStyles } from "./cancelled-styles-reducers";
import { timeline } from "reducers/timeline";
import { INotificationsState, notifications } from "reducers/notifications";
import { IMerchantState, merchants } from "./merchants-reducers";
import { IInventoryState, inventory } from "./inventory";
import { subscriptions } from "./subscriptions";
import {
    IMediaState,
    productMedia,
    IMediaManifestState,
    mediaManifest,
    IMediaAssetsState,
    productMediaAssets,
} from "reducers/product-media-reducers";
import { IUnlinkedMediaState, unlinkedMedia } from "reducers/unlinked-media-reducers";
import { brandSizeOverrides } from "./brand-size-overrides";
import { productSizeOverrides } from "./size-overrides";
import { sizeFamilies } from "./size-families";
import { sizeCompliance } from "./size-compliance";
import { merchantAllocationDimension } from "./merchant-allocation-dimension";
import chargebacks from "./chargebacks";
import statements from "./statements";
import transactions from "./transactions";
import paymentSummary from "./payment-summary";
import { IUploadBuyersState, uploadBuyers } from "./upload-buyers-reducer";
import { colorSwatches, IColorSwatchesState } from "reducers/color-swatches";
import { catalogImport, IImportCatalogState } from "reducers/catalog-import-reducer";
import { brandSettingsImageFormats } from "./brand-settings-image-formats";
import { exportNotifications } from "./export-notifications";
import { priceSheetsUpload } from "./price-sheets-reducers";
import { IExportState, productDataUpload, productDataExport } from "./product-data-reducers";
import { productImagesUpload } from "./product-images-reducers";
import { companyDataUpload } from "./company-data-reducers";
import { buyerDataUpload } from "./buyer-data-reducers";
import { atsCustomTemplates, IAtsCustomTemplatesState } from "./ats-custom-template-reducers";
import { atsExport, IAtsCustomTemplateExport } from "./ats-export-reducers";
import {
    sizePacksUpload,
    sizePacksExport,
    IExportState as ISizePacksExportState,
    IUploadState as ISizePacksUploadState,
} from "./size-packs-reducers";
import { IUploadState } from "./base-upload-reducers";
import { merchantAdminAccount, IMerchantAdminAccountState } from "./merchant-admin-account-reducers";
import { brandsData, IBrandsDataState } from "./brands-data-reducers";
import { merchantAdminMemberList, IMerchantAdminMemberListState } from "./merchant-admin-member-list-reducers";
import { merchantAdminMemberDetails, IMerchantAdminMemberDetailsState } from "./merchant-admin-member-details-reducers";
import { accessControlReducer as accessControl, IAccessControlState } from "./access-control-reducers";
import { variants, IVariantsState } from "reducers/variants";
import { IPosState, pos } from "reducers/pos";
import { lsid } from "reducers/lsid-reducers";
import { lsidUserinfo } from "reducers/lsid-userinfo-reducers";
import { IWatermarking, watermarking } from "reducers/watermarking";
import { IFetchStoreState } from "./base-reducers";
import { deliveryWindows, IDeliveryWindowsState } from "./delivery-windows";
import { financialTarget } from "./collection-target";
import { FinancialTargetReduxState } from "./collection-target/types";
import { merchantEnablements } from "./merchant-enablements";
import { merchantOrganization } from "./merchant-organization";
import { cartNotificationsReducer as cartNotifications, ICartNotificationsState } from "./cart-notifications";
import type { IShippingRatesState } from "types/shipping";
import { insights, type IInsightsState } from "./insights-reducer";
import { companies, type ICompaniesState } from "./companies";

export * from "./types";

export interface IStore {
    scope: IScope;
    dashboard: IDashboard;
    warning: IWarningState;
    dataflow: IDataFlowState;
    working: IWorkingState;
    inventoryData: IInventoryDataState;
    inventory: IInventoryState;
    cancelledStyles: ICancelledStylesState;
    assortmentBuyers: IAssortmentBuyersState;
    assortmentSuperusers: IAssortmentSuperusersState;
    merchants: IMerchantState;
    contacts: IContactsState;
    productMedia: IMediaState;
    productMediaAssets: IMediaAssetsState;
    unlinkedMedia: IUnlinkedMediaState;
    mediaManifest: IMediaManifestState;
    notifications: INotificationsState;
    uploadBuyers: IUploadBuyersState;
    colorSwatches: IColorSwatchesState;
    catalogImport: IImportCatalogState;
    cartGridView: CartGridView;
    atsCustomTemplates: IAtsCustomTemplatesState;
    atsExport: IAtsCustomTemplateExport;
    companyDataUpload: IUploadState;
    buyerDataUpload: IUploadState;
    inventoryDataUpload: IUploadState;
    productDataUpload: IUploadState;
    productDataExport: IExportState;
    priceSheetsUpload: IUploadState;
    productImagesUpload: IUploadState;
    sizePacksUpload: ISizePacksUploadState;
    sizePacksExport: ISizePacksExportState;
    merchantAdminAccount: IMerchantAdminAccountState;
    merchantAdminMemberList: IMerchantAdminMemberListState;
    merchantAdminMemberDetails: IMerchantAdminMemberDetailsState;
    brandsData: IBrandsDataState;
    customization: ICustomizationState;
    accessControl: IAccessControlState;
    variants: IVariantsState;
    watermarking: IFetchStoreState<IWatermarking>;
    pos: IPosState;
    deliveryWindows: IDeliveryWindowsState;
    ldFlags: LdFlagsState;
    dimensionsPanel: DimensionsPanelCompositeReduxState;
    financialTarget: FinancialTargetReduxState;
    cartNotifications: ICartNotificationsState;
    shippingRates: IShippingRatesState;
    loader: ILoaderState;
    insights: IInsightsState;
    companies: ICompaniesState;
    [reducer: string]: any;
}

const appReducer = combineReducers({
    authentication,
    accountCarrierServiceWarehouseService,
    activeList,
    app,
    attachments,
    brandCategories,
    brandProfile,
    brandProfiles,
    brandedLogin,
    bulkItems,
    bulkManager,
    buyerDashboard,
    campaign,
    campaignRecipients,
    campaigns,
    cart,
    catalog,
    catalogShares,
    catalogs,
    catalogImport,
    cartGridView,
    chargebacks,
    collection,
    collectionBudgets,
    collectionPermissions,
    collectionSidePanel,
    collections,
    collectionChat,
    contactGroup,
    contactGroupRecipients,
    connectionRequests,
    collectionItemDisplayPreference,
    collectionRollupObjects,
    dashboard,
    dnd,
    entities,
    entries,
    events,
    filters,
    globalProducts,
    internalNotes,
    ldFlags,
    lifecycleScripts,
    inventoryArriving,
    genericFilters,
    filtersGroups,
    item,
    items,
    legacyRetailers,
    masterSchema,
    merchantEnablements,
    merchantOrganization,
    merchantAllocationDimension,
    mutate,
    dimensionsPanel,
    orderSummary,
    pageLayout,
    pageLayouts,
    product,
    products,
    promotion,
    promotions,
    paymentSummary,
    queries,
    retailerProfileForm,
    routing,
    schemas,
    schemaMap,
    scope,
    shippingAccountCarriers,
    shippingAccounts,
    shippingCarrierServices,
    shippingCarriers,
    shippingPromotions,
    snackbars,
    statements,
    template,
    templates,
    timeline,
    notifications,
    transactions,
    users,
    userLookup,
    activeStatus,
    utilities,
    views,
    whiteboard,
    whiteboardPermissions,
    whiteboards,
    assortmentBuyers,
    assortmentSuperusers,
    merchants,
    dataflow,
    contacts,
    recentOrders,
    shippingRates,
    companyDiscounts,
    warning,
    working,
    inventoryData,
    cancelledStyles,
    uploadBuyers,
    subscriptions,
    productMedia,
    productMediaAssets,
    unlinkedMedia,
    mediaManifest,
    sizeFamilies,
    productSizeOverrides,
    brandSizeOverrides,
    sizeCompliance,
    colorSwatches,
    companyDataUpload,
    buyerDataUpload,
    orderPromotions,
    inventoryDataUpload,
    productDataUpload,
    productDataExport,
    priceSheetsUpload,
    sizePacksUpload,
    sizePacksExport,
    productImagesUpload,
    brandSettingsImageFormats,
    atsCustomTemplates,
    atsExport,
    exportNotifications,
    merchantAdminAccount,
    merchantAdminMemberList,
    merchantAdminMemberDetails,
    brandsData,
    customization,
    accessControl,
    variants,
    pos,
    lsid,
    lsidUserinfo,
    watermarking,
    deliveryWindows,
    financialTarget,
    inventory,
    cartConfirmation,
    smartOrders,
    cartNotifications,
    loader,
    insights,
    companies,
} as any);

const rootReducer = (state, action) => {
    if (action.type === USER_LOGOUT) {
        state = undefined;
    }

    return appReducer(state, action);
};

export default rootReducer;
